import { ExpandableImage } from '@/components/expandable-image';
import { MobileContainer } from '@/components/workflow/mobile-container';
import { MobileHeader } from '@/components/workflow/mobile-header';
import { Badge, Button, Flex, Image, Slider, Space, Text, Title } from '@mantine/core';
import NumberFlow from '@number-flow/react';
import { IconCamera, IconPhotoPlus, IconPlus, IconReceipt } from '@tabler/icons-react';
import { CustomerValue, ManualLoadCreateFormData } from '../context';
import { useBaleInboundContext } from '../hook';
import styles from '../styles.module.css';

export const BaleInboundLoadEvaluatePage = () => {
	const { setPage, workflowType, formData, handleManualCreateInput } = useBaleInboundContext();

	return (
		<MobileContainer
			header={
				<MobileHeader
					title="Evaluate Contents"
					onBack={() => setPage(workflowType === 'MANUAL' ? 'MANUAL_CREATE:ROOT' : 'RECEIVING')}
				/>
			}
		>
			<Flex direction="column" gap="md" pb={100} py="md">
				<ContaminationSection />
				<LoadPhotosSection
					photos={formData.loadPhotos}
					onAddPhoto={() => setPage('ADD_LOAD_PHOTOS')}
					onDeletePhoto={(photo) => {
						handleManualCreateInput(
							'loadPhotos',
							formData.loadPhotos.filter((p) => p !== photo)
						);
					}}
				/>
				{workflowType === 'DEFAULT' && (
					<DocketSection
						formData={formData}
						onInput={handleManualCreateInput}
						onAddPhoto={() => setPage('DOCKET_PHOTO')}
					/>
				)}
			</Flex>
		</MobileContainer>
	);
};

const ContaminationSection = () => {
	const { contamination, setContamination } = useBaleInboundContext();
	return (
		<Flex
			style={{
				borderRadius: 6,
				boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',
				border: '1px solid #E0E0E0',
			}}
			direction="column"
			bg="white"
			gap="sm"
			p="md"
			mx="md"
		>
			<Flex w="100%" pb="sm" align="start" justify="space-between">
				<Text fw={600}>Contamination</Text>
				<Text size="xl" mt={-10} fw={600}>
					<NumberFlow value={contamination} suffix="%" />
				</Text>
			</Flex>
			<Slider
				color="blue"
				marks={[
					{ value: 20, label: '20%' },
					{ value: 50, label: '50%' },
					{ value: 80, label: '80%' },
				]}
				label={null}
				onChange={setContamination}
				value={contamination}
				step={5}
				min={0}
				max={100}
				thumbSize={28}
			/>
			<Space h="sm" />
		</Flex>
	);
};

const LoadPhotosSection: React.FC<{
	photos: string[];
	onAddPhoto: () => void;
	onDeletePhoto: (photo: string) => void;
}> = ({ photos, onAddPhoto, onDeletePhoto }) => (
	<Flex direction="column" w="100%" gap="sm">
		{/* Section Header */}
		<Flex align="center" justify="space-between" w="100%" px="md">
			<Title order={2}>Load Photos</Title>
			<Badge bg={photos.length > 0 ? 'green' : 'red'} size="md" fw={500}>
				{photos.length > 0 ? 'Okay' : 'Required'}
			</Badge>
		</Flex>

		{/* Photos Display or Empty State */}
		{photos.length > 0 && (
			<Flex px="md" className={styles.horizontalScrollContainer}>
				{photos.map((photo, i) => (
					<Flex pr="sm">
						<ExpandableImage
							onDelete={() => onDeletePhoto(photo)}
							key={i}
							src={photo}
							id={`load-photo-${i}`}
						/>
					</Flex>
				))}
			</Flex>
		)}

		{photos.length === 0 && (
			<Flex px="md">
				<Flex
					style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
					w="100%"
					p="md"
					direction="column"
					c="gray"
					justify="center"
					align="center"
					gap="sm"
				>
					<IconPhotoPlus size={48} />
					<Text size="md">Add load photos</Text>
				</Flex>
			</Flex>
		)}

		{/* Add Photo Button */}
		<Flex px="md" gap="md">
			<Button
				onClick={onAddPhoto}
				fullWidth
				variant="filled"
				leftSection={<IconCamera size={16} />}
			>
				Add Photo
			</Button>
		</Flex>
	</Flex>
);

const DocketSection: React.FC<{
	formData: ManualLoadCreateFormData;
	onInput: (
		key: keyof ManualLoadCreateFormData,
		value: string | number | string[] | CustomerValue | null
	) => void;
	onAddPhoto: () => void;
}> = ({ formData, onInput, onAddPhoto }) => {
	const isComplete = formData.docketPhoto && formData.docketId;

	return (
		<Flex direction="column" px="md" w="100%" gap="lg">
			{/* Section Header */}
			<Flex align="center" justify="space-between" w="100%">
				<Title order={2}>Docket</Title>
				<Badge bg={isComplete ? 'green' : 'red'} size="md" fw={500}>
					{isComplete ? 'Okay' : 'Required'}
				</Badge>
			</Flex>

			{/* Photo Display or Empty State */}
			{formData.docketPhoto ? (
				<Flex className={styles.docketPhotoContainer}>
					<Image src={formData.docketPhoto} alt="Docket Photo" />
				</Flex>
			) : (
				<Flex
					w="100%"
					direction="column"
					style={{ border: '1px dashed var(--mantine-color-gray-6)', borderRadius: '6px' }}
					c="gray"
					p="md"
					justify="center"
					align="center"
					gap="sm"
				>
					<IconReceipt size={48} />
					<Text size="md">Add docket photo</Text>
				</Flex>
			)}

			{/* Photo Action Button */}
			<Flex gap="md">
				{formData.docketPhoto ? (
					<Button
						onClick={() => onInput('docketPhoto', null)}
						fullWidth
						variant="filled"
						bg="gray"
						leftSection={<IconPlus size={16} />}
					>
						Discard
					</Button>
				) : (
					<Button
						onClick={onAddPhoto}
						fullWidth
						variant="filled"
						leftSection={<IconCamera size={16} />}
					>
						Add Photo
					</Button>
				)}
			</Flex>
		</Flex>
	);
};
